import { useState } from 'react';
import { useBrowserStore } from '@/stores/browserStore';
import { API_BASE } from '@/lib/config';
import { getToken } from '@/stores/authStore';
import { Globe, Camera, RefreshCw, Navigation, ExternalLink, Copy, Check, Loader2, Download, Link } from 'lucide-react';

function SetupPanel() {
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [loadingCode, setLoadingCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleGetPairingCode = async () => {
    setLoadingCode(true);
    try {
      const token = getToken();
      let resp: Response;
      if (token) {
        resp = await fetch(`${API_BASE}/browser/pair`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': '' },
        });
      } else {
        resp = new Response(null, { status: 401 });
      }
      if (!resp.ok) {
        resp = await fetch(`${API_BASE}/browser/pair`);
      }
      if (!resp.ok) throw new Error('Failed to get pairing code');
      const data = await resp.json();
      setPairingCode(data.code);
    } catch (err) {
      console.error('Failed to get pairing code:', err);
    } finally {
      setLoadingCode(false);
    }
  };

  const handleCopy = () => {
    if (pairingCode) {
      navigator.clipboard.writeText(pairingCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const resp = await fetch(`${API_BASE}/browser/extension/download`);
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: 'Download failed' }));
        throw new Error(err.error || 'Download failed');
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'zac-browser-extension.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Extension download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col items-center py-8">
      <Globe className="h-10 w-10 text-muted-foreground/50" />
      <h2 className="mt-3 text-base font-medium text-foreground">Connect Browser Extension</h2>
      <p className="mt-1 text-sm text-muted-foreground text-center max-w-sm">
        Install the Chrome extension and pair it to control Zoom admin pages.
      </p>

      {/* Two-column steps */}
      <div className="mt-6 grid grid-cols-2 gap-4 w-full max-w-md">
        {/* Step 1: Install */}
        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[11px] font-medium text-muted-foreground">1</div>
            <span className="text-sm font-medium text-foreground">Install</span>
          </div>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="w-full flex items-center justify-center gap-2 rounded-md bg-secondary px-3 py-2 text-sm text-foreground hover:bg-secondary/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {downloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            {downloading ? 'Preparing...' : 'Download .zip'}
          </button>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Unzip, open <code className="px-1 py-0.5 bg-muted rounded text-[10px]">chrome://extensions</code>, enable Developer Mode, click Load Unpacked.
          </p>
        </div>

        {/* Step 2: Pair */}
        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[11px] font-medium text-muted-foreground">2</div>
            <span className="text-sm font-medium text-foreground">Pair</span>
          </div>
          {pairingCode ? (
            <div className="flex items-center justify-center gap-2">
              <code className="px-3 py-2 bg-muted rounded-md text-base font-mono tracking-[0.25em] text-foreground">
                {pairingCode}
              </code>
              <button
                onClick={handleCopy}
                className="p-1.5 rounded hover:bg-secondary transition-colors"
                title="Copy code"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-400" />
                ) : (
                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                )}
              </button>
            </div>
          ) : (
            <button
              onClick={handleGetPairingCode}
              disabled={loadingCode}
              className="w-full flex items-center justify-center gap-2 rounded-md bg-secondary px-3 py-2 text-sm text-foreground hover:bg-secondary/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loadingCode ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Link className="h-3.5 w-3.5" />
              )}
              {loadingCode ? 'Generating...' : 'Get Code'}
            </button>
          )}
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            {pairingCode
              ? 'Enter this code in the extension popup. Expires in 5 min.'
              : 'Generate a code, then enter it in the extension popup to pair.'}
          </p>
        </div>
      </div>
    </div>
  );
}

function ConnectedPanel() {
  const {
    tabs,
    activeTabId,
    setActiveTab,
    snapshotPending,
    requestSnapshot,
    requestAction,
    openTab,
    isPolling,
    startPolling,
  } = useBrowserStore();

  const [navUrl, setNavUrl] = useState('');
  const [newTabUrl, setNewTabUrl] = useState('');

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-green-400" />
          <h2 className="text-sm font-medium text-foreground">Extension Connected</h2>
          <span className="text-xs text-muted-foreground">
            {tabs.length} tab{tabs.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button
          onClick={() => { if (!isPolling) startPolling(); }}
          className="p-1.5 rounded hover:bg-secondary transition-colors"
          title="Refresh status"
        >
          <RefreshCw className={`h-4 w-4 text-muted-foreground ${isPolling ? 'animate-spin-slow' : ''}`} />
        </button>
      </div>

      {/* Tabs List */}
      {tabs.length > 0 ? (
        <div className="space-y-1">
          {tabs.map((tab) => {
            const isActive = tab.tabId === activeTabId;
            let hostname: string;
            try { hostname = new URL(tab.url).hostname; } catch { hostname = tab.url; }
            return (
              <button
                key={tab.tabId}
                onClick={() => setActiveTab(tab.tabId)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-foreground'
                    : 'hover:bg-secondary/50 text-muted-foreground'
                }`}
              >
                <Globe className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="text-sm truncate flex-1">{hostname}</span>
                {isActive && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary text-primary-foreground">
                    Active
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-3">
          <p className="text-sm text-muted-foreground">No tabs attached</p>
          <p className="text-xs text-muted-foreground">
            Attach a tab from the extension popup or open one below
          </p>
        </div>
      )}

      {/* Open New Tab */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newTabUrl}
          onChange={(e) => setNewTabUrl(e.target.value)}
          placeholder="Open new tab — https://..."
          className="flex-1 bg-secondary border-0 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newTabUrl.trim()) {
              openTab(newTabUrl.trim());
              setNewTabUrl('');
            }
          }}
        />
        <button
          onClick={() => {
            if (newTabUrl.trim()) {
              openTab(newTabUrl.trim());
              setNewTabUrl('');
            }
          }}
          disabled={!newTabUrl.trim()}
          className="p-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Open new tab"
        >
          <ExternalLink className="h-4 w-4" />
        </button>
      </div>

      {/* Active tab controls */}
      {activeTabId && (
        <div className="flex gap-2">
          <input
            type="text"
            value={navUrl}
            onChange={(e) => setNavUrl(e.target.value)}
            placeholder="Navigate to — https://..."
            className="flex-1 bg-secondary border-0 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && navUrl.trim()) {
                requestAction(activeTabId, {
                  type: 'navigate',
                  url: navUrl.trim(),
                  preview: `Navigate to ${navUrl.trim()}`,
                });
                setNavUrl('');
              }
            }}
          />
          <button
            onClick={() => {
              if (navUrl.trim()) {
                requestAction(activeTabId, {
                  type: 'navigate',
                  url: navUrl.trim(),
                  preview: `Navigate to ${navUrl.trim()}`,
                });
                setNavUrl('');
              }
            }}
            disabled={!navUrl.trim()}
            className="p-2 rounded-lg bg-secondary text-foreground hover:bg-secondary/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Navigation className="h-4 w-4" />
          </button>
          <button
            onClick={() => requestSnapshot(activeTabId)}
            disabled={snapshotPending}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-secondary text-foreground hover:bg-secondary/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Capture snapshot"
          >
            {snapshotPending ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Camera className="h-4 w-4" />
            )}
            <span className="text-sm">Snapshot</span>
          </button>
        </div>
      )}
    </div>
  );
}

export function ConnectionPanel() {
  const extensionConnected = useBrowserStore((s) => s.extensionConnected);

  if (!extensionConnected) {
    return <SetupPanel />;
  }

  return <ConnectedPanel />;
}
